import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";

import Accordion from "@gx/dls/accordion/Accordion";
import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import SelectInput, { SelectInputOption } from "@gx/dls/inputs/SelectInput";
import TextAreaInput from "@gx/dls/inputs/TextAreaInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { CreateProjectRequestSchema } from "@gx/ts-contracts/services/ontology/api/project_pb";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import { PROJECT_SYNC_SCHEDULE_OPTIONS } from "@/pages/projects/forms/create/constants";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";

const ProjectConfigureStep = () => {
  const { state, dispatch } = useCreateProjectFormContext();

  const handleNameChange = useCallback(
    (value: string) => {
      dispatch({
        type: CreateProjectFormActionType.SET_CREATE_PROJECT_REQUEST,
        payload: create(CreateProjectRequestSchema, {
          ...state.createProjectRequest,
          name: value,
        }),
      });
    },
    [dispatch, state.createProjectRequest],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      dispatch({
        type: CreateProjectFormActionType.SET_CREATE_PROJECT_REQUEST,
        payload: create(CreateProjectRequestSchema, {
          ...state.createProjectRequest,
          description: value,
        }),
      });
    },
    [dispatch, state.createProjectRequest],
  );

  const handleProjectSyncScheduleChange = useCallback(
    (cronExpression: string) => {
      dispatch({
        type: CreateProjectFormActionType.SET_PROJECT_SYNC_CRON_EXPRESSION,
        payload: cronExpression,
      });
    },
    [dispatch],
  );

  const selectedProjectSyncScheduleOption = useMemo(
    () =>
      PROJECT_SYNC_SCHEDULE_OPTIONS.find(
        (option) => option.cronExpression === state.projectSyncCronExpression,
      ) ?? PROJECT_SYNC_SCHEDULE_OPTIONS[0],
    [state.projectSyncCronExpression],
  );

  const scheduleOptions = useMemo<SelectInputOption[]>(
    () =>
      PROJECT_SYNC_SCHEDULE_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        value: option.cronExpression,
      })),
    [],
  );

  const selectedScheduleOption = useMemo<SelectInputOption>(
    () =>
      scheduleOptions.find((option) => option.value === state.projectSyncCronExpression) ??
      scheduleOptions[0],
    [scheduleOptions, state.projectSyncCronExpression],
  );

  const handleScheduleOptionChange = useCallback(
    (option: SelectInputOption) => {
      handleProjectSyncScheduleChange(typeof option.value === "string" ? option.value : "");
    },
    [handleProjectSyncScheduleChange],
  );

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
      <TextInput
        placeholder="Enter a name for the project..."
        label="Name"
        labelTooltip="Choose a name that reflects the project's purpose or main data sources."
        value={state.createProjectRequest.name}
        onChange={handleNameChange}
        isRequired
        fillWidth
        autoFocus
      />
      <TextAreaInput
        placeholder="Enter a description for the project..."
        label="Description"
        labelTooltip="Help your team understand the project's purpose and scope."
        value={state.createProjectRequest.description ?? ""}
        onChange={handleDescriptionChange}
        lines={12}
        fillWidth
      />
      <Accordion
        header="Advanced configuration"
        metric={
          <Text variant={TextVariant.SUCCESS} size={TextSize.BODY_SM}>
            {selectedProjectSyncScheduleOption.label}
          </Text>
        }
        isOpenInitial={false}
      >
        <FlexWrapper direction={FlexDirection.COLUMN} gap={8} fillWidth>
          <SelectInput
            label="Sync schedule"
            options={scheduleOptions}
            value={selectedScheduleOption}
            onChange={handleScheduleOptionChange}
            fillWidth
          />
        </FlexWrapper>
      </Accordion>
    </FlexWrapper>
  );
};

export default ProjectConfigureStep;
